import { useAppSelector } from '@hooks/reduxHooks';

import { flowChatTypes } from './constants';
import { IActionJson, IAiJson, IFlowNode } from './models';

type CustomNode = {
  oldId: string;
  name: string;
  inputType: string;
  dataJson?: string;
  options?: { name?: string; description?: string }[];
  type: keyof typeof flowChatTypes;
  [key: string]: any;
};

export const getEmptyFieldsInNodes = (nodes: any[]) =>
  nodes.map(node => {
    const emptyFields = [];
    const { data } = node;

    if (!data?.name?.trim()) emptyFields.push('data.name');
    if (!data?.description?.trim()) emptyFields.push('data.description');

    if (data?.json && typeof data.json === 'object') {
      Object.entries(data.json).forEach(([key, value]) => {
        if ((typeof value === 'string' && !value.trim()) || value === null || value === undefined) {
          emptyFields.push(`data.json.${key}`);
        }
      });
    }

    return {
      nodeId: node.id,
      isInvalid: emptyFields.length > 0,
      emptyFields
    };
  });

export const mergeNodes = (initialNodes: IFlowNode[], newNodes: IFlowNode[]) => {
  const nodeMap = new Map<string, IFlowNode>();

  initialNodes.forEach(node => {
    nodeMap.set(node?.id, node);
  });

  newNodes.forEach(newNode => {
    nodeMap.set(newNode.id, newNode);
  });

  return Array.from(nodeMap.values());
};

export const getInvalidNodeIds = (nodes: CustomNode[]) => {
  const invalidIds: string[] = [];

  nodes.forEach(node => {
    const hasEmptyName = !node.name?.trim();
    const hasEmptyInputType = !node.inputType?.trim();

    let hasInvalidDataJson = false;

    if (node.dataJson) {
      try {
        const parsed = JSON.parse(node.dataJson);

        hasInvalidDataJson = Object.values(parsed).some(val => {
          const isPlayAudioInvalid = node?.type === 'PLAY_AUDIO' && val === null;

          const isEmptyString = typeof val === 'string' && val.trim() === '';

          const isCreateNoteInvalid =
            node?.type === 'ACTION' &&
            parsed?.action === 'CREATE_NOTE' &&
            !(parsed?.leadNoteDto && 'text' in parsed.leadNoteDto);

          const isSetValueInvalid =
            node?.type === 'ACTION' &&
            parsed?.action === 'SET_VALUE' &&
            (!parsed?.setFieldDto || (!('value' in parsed.setFieldDto) && !('values' in parsed.setFieldDto)));

          const isCreateTaskInvalid =
            node?.type === 'ACTION' &&
            parsed?.action === 'CREATE_TASK' &&
            (!parsed?.createTaskDto ||
              !('assignedById' in parsed.createTaskDto) ||
              !('leadTaskTypeId' in parsed.createTaskDto) ||
              !('text' in parsed.createTaskDto));

          if (isPlayAudioInvalid || isEmptyString || isCreateNoteInvalid || isSetValueInvalid || isCreateTaskInvalid) {
            return true;
          }
          return false;
        });
      } catch (err) {
        hasInvalidDataJson = true;
      }
    }

    const hasInvalidOption = node.options?.some(opt => !opt.name?.trim() || !opt.description?.trim());

    if (hasEmptyName || hasEmptyInputType || hasInvalidDataJson || hasInvalidOption) {
      invalidIds.push(node.oldId);
    }
  });

  return invalidIds;
};

export const generateRequestBodyFromNodes = () => {
  const nodes = useAppSelector(state => state.aiAgentReducer.nodes);

  const cleaned = (obj?: Record<string, any>) =>
    Object.fromEntries(Object.entries(obj || {}).filter(([_, value]) => value !== ''));

  const defaultDeadline = 'NOW';

  return nodes
    ?.map(node => {
      const { id, type, position, data } = node;
      const json = data?.json;

      if (!json || !type) return null;

      const isLLMType = type === 'LLM' || type === 'QUESTION_CLASSIFIER';
      const aiJson = isLLMType ? (json as IAiJson) : null;
      const actionJson = json as IActionJson;

      let dataJson: any = json;

      if (type === 'ACTION') {
        const { action, setFieldDto, createTaskDto, leadNoteDto } = actionJson;

        dataJson = {
          action,
          ...(action === 'SET_VALUE' && {
            setFieldDto: cleaned(setFieldDto)
          }),
          ...(action === 'CREATE_TASK' && {
            createTaskDto: {
              ...cleaned(createTaskDto),
              deadLineAfterStatus: createTaskDto?.deadLineAfterStatus || defaultDeadline
            }
          }),
          ...(action === 'CREATE_NOTE' && {
            leadNoteDto: cleaned(leadNoteDto)
          })
        };
      } else if (type === 'PLAY_AUDIO') {
        dataJson = {
          fileId: (json as { fileId: number })?.fileId
        };
      }

      const result = {
        ...(!id?.includes('new') && { id: String(id) }),
        oldId: id,
        name: data?.name,
        description: data?.description,
        x: position?.x,
        y: position?.y,
        type,
        ...(isLLMType && {
          useOldConversation: aiJson?.useOldConversation,
          inputType: aiJson?.inputType,
          ai: aiJson?.ai,
          tokenLimit: aiJson?.tokenLimit
        }),
        ...(!isLLMType && { inputType: 'NONE' }),
        ...(type === 'TTS' && { ai: 'YANDEX' }),
        ...(type === 'QUESTION_CLASSIFIER' && { options: data?.options }),
        ...(['PLAY_AUDIO', 'TTS', 'SEND_MESSAGE', 'LLM', 'ACTION', 'QUESTION_CLASSIFIER'].includes(type)
          ? { dataJson: JSON.stringify(dataJson) }
          : {})
      };

      return result;
    })
    .filter(Boolean);
};
